import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API, formatCurrency, formatDate, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, FileText, ChevronRight, Filter } from 'lucide-react';

const QuotesPage = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    }
    setLoading(false);
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || quote.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-12 bg-muted rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Devis</h1>
          <Button 
            onClick={() => navigate(canWrite() ? '/quotes/new' : '/subscription')}
            className="h-12 px-6 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
            data-testid="new-quote-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Nouveau</span>
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
              data-testid="quote-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 h-12 bg-muted/50 border-transparent" data-testid="quote-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="envoye">Envoyé</SelectItem>
              <SelectItem value="accepte">Accepté</SelectItem>
              <SelectItem value="refuse">Refusé</SelectItem>
              <SelectItem value="expire">Expiré</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quote List */}
        {filteredQuotes.length > 0 ? (
          <div className="space-y-3">
            {filteredQuotes.map((quote) => (
              <Card 
                key={quote.id} 
                className="bg-card border-white/10 card-hover cursor-pointer"
                onClick={() => navigate(`/quotes/${quote.id}`)}
                data-testid={`quote-card-${quote.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-sm text-muted-foreground">
                        {quote.quote_number}
                      </span>
                      <span className={`status-badge ${STATUS_COLORS[quote.status]}`}>
                        {STATUS_LABELS[quote.status]}
                      </span>
                    </div>
                    <p className="font-semibold truncate">{quote.client_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Créé le {formatDate(quote.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{formatCurrency(quote.total_ttc)}</p>
                    <p className="text-xs text-muted-foreground">TTC</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-white/10">
            <CardContent className="empty-state">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm || filterStatus !== 'all' ? 'Aucun résultat' : 'Aucun devis'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterStatus !== 'all' ? 'Essayez avec d\'autres critères' : 'Créez votre premier devis'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button
                  onClick={() => navigate(canWrite() ? '/quotes/new' : '/subscription')}
                  className="bg-primary text-primary-foreground hover:bg-accent rounded-full"
                  data-testid="create-first-quote-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuotesPage;
